class TabbedCollectionComponent extends HTMLElement {
  connectedCallback() {
    this.tabBar = this.querySelector('[data-tab-bar]');

    if (!this.tabBar) return;

    this.tabBar.addEventListener('click', this.handleTabClick.bind(this));

    this.prevArrow = this.querySelector('[data-direction="prev"]');
    this.nextArrow = this.querySelector('[data-direction="next"]');
    this.shopAllBtn = this.querySelector('[data-shop-all]');
    this.shopAllMobile = this.querySelector('[data-shop-all-mobile]');

    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => this.scrollCarousel('prev'));
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => this.scrollCarousel('next'));
    }

    this.bindCarouselScrollListeners();

    // The arrow disabled-state depends on each carousel's scrollWidth, which is
    // only accurate once product images have laid out. Measuring only here can
    // read scrollWidth ~= clientWidth and wrongly disable both arrows — and a
    // disabled <button> swallows clicks, so they never recover. Re-measure once
    // layout settles (rAF + window load) and whenever a carousel resizes.
    requestAnimationFrame(() => this.updateArrowStates());
    window.addEventListener('load', () => this.updateArrowStates(), { once: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  handleTabClick(event) {
    const button = event.target.closest('[data-tab-index]');

    if (!button) return;

    const index = button.getAttribute('data-tab-index');
    this.activateTab(index);
  }

  activateTab(index) {
    const tabs = this.querySelectorAll('[data-tab-index]');
    const panels = this.querySelectorAll('[data-tab-panel]');

    for (const tab of tabs) {
      const isActive = tab.getAttribute('data-tab-index') === index;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    for (const panel of panels) {
      const isActive = panel.getAttribute('data-tab-panel') === index;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
    }

    const activeTab = this.querySelector(`[data-tab-index="${index}"]`);

    if (activeTab) {
      const collectionUrl = activeTab.getAttribute('data-collection-url') || '';
      const buttonLabel = activeTab.getAttribute('data-button-label') || '';

      if (this.shopAllBtn) {
        this.shopAllBtn.href = collectionUrl;
        this.shopAllBtn.textContent = buttonLabel;
      }

      if (this.shopAllMobile) {
        this.shopAllMobile.href = collectionUrl;
        this.shopAllMobile.textContent = buttonLabel;
      }
    }

    const activePanel = this.querySelector(`[data-tab-panel="${index}"]`);

    if (activePanel) {
      const carousel = activePanel.querySelector('.tabbed-collection__carousel');

      if (carousel) {
        carousel.scrollLeft = 0;
      }
    }

    this.updateArrowStates();
  }

  getActiveCarousel() {
    const activePanel = this.querySelector('.tabbed-collection__panel.active');

    if (!activePanel) return null;

    return activePanel.querySelector('.tabbed-collection__carousel');
  }

  scrollCarousel(direction) {
    const carousel = this.getActiveCarousel();

    if (!carousel) return;

    const firstItem = carousel.querySelector('.tabbed-collection__product-item');

    if (!firstItem) return;

    const style = getComputedStyle(carousel);
    const gap = parseFloat(style.gap) || 0;
    const scrollAmount = firstItem.offsetWidth + gap;

    carousel.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    });
  }

  updateArrowStates() {
    const carousel = this.getActiveCarousel();

    if (!carousel || !this.prevArrow || !this.nextArrow) return;

    // Carousel not laid out yet (e.g. hidden tab panel or images unloaded):
    // don't compute a disabled-state from a zero-width measurement.
    if (carousel.scrollWidth === 0) return;

    const scrollLeft = Math.round(carousel.scrollLeft);
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;

    this.prevArrow.disabled = scrollLeft <= 0;
    this.nextArrow.disabled = scrollLeft >= maxScroll - 2;
  }

  bindCarouselScrollListeners() {
    this.resizeObserver = new ResizeObserver(() => this.updateArrowStates());

    const carousels = this.querySelectorAll('.tabbed-collection__carousel');

    for (const carousel of carousels) {
      carousel.addEventListener('scroll', () => {
        this.updateArrowStates();
      }, { passive: true });
      this.resizeObserver.observe(carousel);
    }
  }
}

customElements.define('tabbed-collection-component', TabbedCollectionComponent);
